import React, { useEffect, useMemo, useState } from "react";
import { FaEdit, FaLayerGroup, FaPlus, FaSearch, FaShieldAlt, FaTrash } from "react-icons/fa";
import api from "../api";
import Modal from "../components/Modal";
import InfoTooltip from "../components/InfoTooltip";
import { formatPermissionName } from "../utils/permissions";

export default function Groups() {
  const [items, setItems] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [form, setForm] = useState({ name: "", permissionIds: [] });
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [permissionFilter, setPermissionFilter] = useState("");

  const load = () => {
    api
      .get("/groups")
      .then((res) => setItems(res.data))
      .catch(() => setItems([]));
    api
      .get("/permissions")
      .then((res) => setPermissions(res.data))
      .catch(() => setPermissions([]));
  };

  useEffect(() => {
    load();
  }, []);

  const sortedPermissions = useMemo(() => {
    const term = permissionFilter.trim().toLowerCase();
    return permissions
      .slice()
      .filter((perm) => {
        if (!term) return true;
        const label = formatPermissionName(perm.name).toLowerCase();
        return label.includes(term);
      })
      .sort((a, b) => {
        const aLabel = formatPermissionName(a.name).toLowerCase();
        const bLabel = formatPermissionName(b.name).toLowerCase();
        return aLabel.localeCompare(bLabel);
      });
  }, [permissions, permissionFilter]);

  const filteredGroups = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    return items.filter((group) => {
      const permissionNames = (group.permissions || [])
        .map((perm) => formatPermissionName(perm.name).toLowerCase());
      return (
        group.name.toLowerCase().includes(term) ||
        permissionNames.some((name) => name.includes(term))
      );
    });
  }, [items, searchTerm]);

  const togglePermission = (id) => {
    setForm((current) => {
      const has = current.permissionIds.includes(id);
      return {
        ...current,
        permissionIds: has
          ? current.permissionIds.filter((permId) => permId !== id)
          : [...current.permissionIds, id],
      };
    });
  };

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm({ name: "", permissionIds: [] });
    setPermissionFilter("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingId) {
        await api.put(`/groups/${editingId}`, form);
      } else {
        await api.post("/groups", form);
      }
      closeModal();
      load();
    } catch (error) {
      const msg = error.response?.data?.error || "Erro ao salvar grupo";
      alert(msg);
    }
  };

  const handleEdit = (group) => {
    setEditingId(group.id);
    setForm({
      name: group.name,
      permissionIds: group.permissions.map((permission) => permission.id),
    });
    setPermissionFilter("");
    setModalOpen(true);
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ name: "", permissionIds: [] });
    setPermissionFilter("");
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Remover grupo?")) {
      try {
        await api.delete(`/groups/${id}`);
        load();
      } catch (error) {
        const msg = error.response?.data?.error || "Erro ao remover grupo";
        alert(msg);
      }
    }
  };

  const totalGroups = items.length;
  const totalPermissions = permissions.length;
  const averagePermissions = totalGroups
    ? Math.round(
        items.reduce((acc, group) => acc + (group.permissions?.length || 0), 0) /
          totalGroups
      )
    : 0;
  const highlightedGroup = useMemo(() => {
    if (!items.length) return null;
    return items
      .slice()
      .sort(
        (a, b) => (b.permissions?.length || 0) - (a.permissions?.length || 0)
      )[0];
  }, [items]);

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
            Governança de acesso
          </p>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-semibold text-slate-900">
              Grupos de usuários
            </h1>
            <InfoTooltip label="Organize permissões e responsabilidades rapidamente." />
          </div>
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-lg transition hover:bg-blue-700"
        >
          <FaPlus />
          Novo grupo
        </button>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="flex items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-blue-800 shadow-sm">
          <FaLayerGroup className="text-xl" />
          <div>
            <p className="text-xs uppercase tracking-wide text-blue-500">
              Grupos ativos
            </p>
            <p className="text-2xl font-semibold">{totalGroups}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-emerald-800 shadow-sm">
          <FaShieldAlt className="text-xl" />
          <div>
            <p className="text-xs uppercase tracking-wide text-emerald-500">
              Permissões disponíveis
            </p>
            <p className="text-2xl font-semibold">{totalPermissions}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-amber-800 shadow-sm">
          <FaSearch className="text-xl" />
          <div>
            <p className="text-xs uppercase tracking-wide text-amber-500">
              Média por grupo
            </p>
            <p className="text-2xl font-semibold">{averagePermissions}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-slate-700 shadow-sm">
          <FaEdit className="text-xl text-slate-500" />
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">
              Destaque
            </p>
            <p className="text-sm font-medium text-slate-700">
              {highlightedGroup
                ? `${highlightedGroup.name} (${highlightedGroup.permissions?.length || 0} permissões)`
                : "Cadastre um grupo para começar"}
            </p>
          </div>
        </div>
      </section>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-md">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Buscar por nome ou permissão"
            className="w-full rounded-full border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span>Resultados</span>
          <span className="inline-flex min-w-[2rem] justify-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
            {filteredGroups.length}
          </span>
          <InfoTooltip
            label={
              filteredGroups.length === 1
                ? 'Um grupo corresponde aos filtros.'
                : `${filteredGroups.length} grupos correspondem aos filtros.`
            }
          />
        </div>
      </div>

      <section className="grid gap-4 lg:grid-cols-2">
        {filteredGroups.length ? (
          filteredGroups.map((group) => (
            <article
              key={group.id}
              className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">{group.name}</h2>
                  <p className="text-xs uppercase tracking-widest text-slate-400">
                    {(group.permissions?.length || 0)} permissões vinculadas
                  </p>
                </div>
                <div className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-600">
                  ID #{String(group.id).padStart(3, "0")}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {group.permissions && group.permissions.length ? (
                  group.permissions.map((permission) => (
                    <span
                      key={permission.id}
                      className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                    >
                      {formatPermissionName(permission.name)}
                    </span>
                  ))
                ) : (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
                    Sem permissões atribuídas
                  </span>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => handleEdit(group)}
                  className="inline-flex items-center gap-2 rounded-full border border-blue-200 px-4 py-2 text-sm font-medium text-blue-600 transition hover:border-blue-300 hover:bg-blue-50"
                >
                  <FaEdit />
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(group.id)}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-200 px-4 py-2 text-sm font-medium text-rose-600 transition hover:border-rose-300 hover:bg-rose-50"
                >
                  <FaTrash />
                  Remover
                </button>
              </div>
            </article>
          ))
        ) : (
          <div className="col-span-full rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500">
            Nenhum grupo encontrado. Ajuste os filtros ou cadastre um novo grupo para começar.
          </div>
        )}
      </section>

      <Modal open={modalOpen} onClose={closeModal}>
        <div className="space-y-6">
          <header className="space-y-1">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
              {editingId ? "Editar grupo" : "Novo grupo"}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-300">
              Defina um nome claro e selecione as permissões que representam a responsabilidade do time.
            </p>
          </header>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="group-name" className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Nome do grupo
              </label>
              <input
                id="group-name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Ex.: Backoffice Financeiro"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-400/40"
                required
              />
            </div>

            <div className="space-y-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    Permissões selecionadas ({form.permissionIds.length})
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-300">
                    Toque para adicionar ou remover permissões deste grupo.
                  </p>
                </div>
                <div className="relative w-full lg:max-w-xs">
                  <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                  <input
                    type="text"
                    value={permissionFilter}
                    onChange={(event) => setPermissionFilter(event.target.value)}
                    placeholder="Filtrar permissões"
                    className="w-full rounded-full border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-600 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:focus:ring-blue-400/40"
                  />
                </div>
              </div>

              <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                {sortedPermissions.length ? (
                  sortedPermissions.map((permission) => {
                    const selected = form.permissionIds.includes(permission.id);
                    return (
                      <button
                        key={permission.id}
                        type="button"
                        onClick={() => togglePermission(permission.id)}
                        aria-pressed={selected}
                        className={`flex w-full items-center justify-between rounded-2xl border px-4 py-2 text-sm transition ${
                          selected
                            ? "border-blue-300 bg-slate-100 text-blue-900 shadow-sm dark:border-blue-300 dark:bg-slate-100 dark:text-blue-900"
                            : "border-slate-200 text-slate-600 hover:border-blue-200 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-blue-400/60 dark:hover:bg-blue-500/10"
                        }`}
                      >
                        <span>{formatPermissionName(permission.name)}</span>
                        {selected && <FaShieldAlt className="text-blue-700 dark:text-blue-700" />}
                      </button>
                    );
                  })
                ) : (
                  <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                    Nenhuma permissão corresponde ao filtro informado.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700"
              >
                {editingId ? "Atualizar grupo" : "Criar grupo"}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}
